import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  api,
  Balanza,
  BalanceGeneral,
  Cliente,
  CongruenciaGlobal,
  CuentaDeCatalogo,
  ErrorDeApi,
  EstadoDeResultados,
  LibroMayor,
  Poliza,
  RenglonDeBalanza,
  ResumenPeriodo,
  SeccionComparativa,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { nombreDePeriodo, periodosDisponibles, type Periodo } from '../periodos'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Los costos y los gastos restan: se presentan entre paréntesis, como en cualquier estado de resultados impreso. */
const enParentesis = (n: number) => `(${dinero(Math.abs(n))})`

type Pestana = 'Balanza' | 'Mayor' | 'Resultados' | 'Balance' | 'Diario'

const PESTANAS: { clave: Pestana; etiqueta: string }[] = [
  { clave: 'Balanza', etiqueta: 'Balanza de comprobación' },
  { clave: 'Mayor', etiqueta: 'Mayor y auxiliares' },
  { clave: 'Resultados', etiqueta: 'Estado de resultados' },
  { clave: 'Balance', etiqueta: 'Balance general' },
]

/** Periodo elegido por el contador para el comparativo (RF-703); sin él, la API compara contra el mes anterior. */
type PeriodoComparativo = Periodo

/**
 * Selector "Comparar con…" del balance general y el estado de resultados (RF-703, cédula F-04):
 * cualquier mes de los tres últimos ejercicios, salvo el periodo en pantalla.
 */
function SelectorDeComparativo({ anio, mes, comparativo, onCambiar }: {
  anio: number; mes: number; comparativo?: PeriodoComparativo; onCambiar: (p?: PeriodoComparativo) => void
}) {
  const periodos: PeriodoComparativo[] = []
  for (let a = anio; a >= anio - 2; a--)
    for (let m = 12; m >= 1; m--)
      if (a !== anio || m !== mes) periodos.push({ anio: a, mes: m })

  return (
    <label style={{ margin: 0 }}>
      Comparar con
      <select
        className="buscador compacta" style={{ width: 'auto', margin: '0 0 0 6px' }}
        value={comparativo ? `${comparativo.anio}-${comparativo.mes}` : ''}
        onChange={(e) => {
          if (!e.target.value) { onCambiar(undefined); return }
          const [a, m] = e.target.value.split('-').map(Number)
          onCambiar({ anio: a, mes: m })
        }}
      >
        <option value="">Mes anterior (predeterminado)</option>
        {periodos.map((p) => (
          <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>{nombreDePeriodo(p.anio, p.mes)}</option>
        ))}
      </select>
    </label>
  )
}

/**
 * Congruencia global del periodo (RF-706): la utilidad del estado de resultados contra la
 * registrada en el capital del balance. La consultan las dos pantallas que la explican.
 */
function useCongruencia(clienteId: string, anio: number, mes: number) {
  const [congruencia, setCongruencia] = useState<CongruenciaGlobal>()
  const [error, setError] = useState('')

  useEffect(() => {
    setError('')
    api.congruenciaGlobal(clienteId, anio, mes)
      .then(setCongruencia)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo calcular la congruencia global (RF-706).'))
  }, [clienteId, anio, mes])

  return { congruencia, errorCongruencia: error }
}

/**
 * Reportes del periodo: balanza de comprobación por niveles, libro mayor/auxiliar, estado de
 * resultados, balance general y libro diario. SRS: RF-701..RF-706 · RN-01, RN-13. Cédula: F-01 a F-06.
 */
export function Reportes() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth() + 1)
  const [pestana, setPestana] = useState<Pestana>('Balanza')
  const [comparativo, setComparativo] = useState<PeriodoComparativo>()
  const [cliente, setCliente] = useState<Cliente>()
  const [error, setError] = useState('')
  const [generandoCierre, setGenerandoCierre] = useState(false)
  const [errorCierre, setErrorCierre] = useState('')
  // La cuenta del mayor vive aquí y no en la pestaña porque "Exportar" está en el encabezado (R11).
  const [cuentaId, setCuentaId] = useState('')
  const [exportando, setExportando] = useState<'excel' | 'pdf'>()
  const [errorExportar, setErrorExportar] = useState('')
  const [polizas, setPolizas] = useState<Poliza[]>()
  const [errorDiario, setErrorDiario] = useState('')
  const [estadoPorPeriodo, setEstadoPorPeriodo] = useState<Record<string, ResumenPeriodo['estado']>>({})

  const periodos = useMemo(() => periodosDisponibles(hoy), [hoy.getFullYear(), hoy.getMonth()])

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el cliente.'))
  }, [clienteId])

  // Estado de cada periodo (RF-404) para poder decir "cerrado" o "abierto (preliminar)" en el
  // selector: un reporte de un mes abierto todavía puede cambiar y el contador tiene que saberlo.
  useEffect(() => {
    if (!clienteId) return
    const anios = [...new Set(periodos.map((p) => p.anio))]
    let vigente = true
    Promise.all(anios.map((a) => api.listarPeriodos(clienteId, a).catch(() => [] as ResumenPeriodo[])))
      .then((listas) => {
        if (!vigente) return
        const mapa: Record<string, ResumenPeriodo['estado']> = {}
        for (const p of listas.flat()) mapa[`${p.anio}-${p.mes}`] = p.estado
        setEstadoPorPeriodo(mapa)
      })
    return () => { vigente = false }
  }, [clienteId, periodos])

  // El libro diario se carga aquí porque su conteo va en el enlace de la barra de pestañas (R13).
  useEffect(() => {
    if (!clienteId) return
    let vigente = true
    setPolizas(undefined)
    setErrorDiario('')
    api.libroDiario(clienteId, anio, mes)
      .then((p) => { if (vigente) setPolizas(p) })
      .catch((e) => { if (vigente) setErrorDiario(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el libro diario.') })
    return () => { vigente = false }
  }, [clienteId, anio, mes])

  async function descargarCierre() {
    setGenerandoCierre(true)
    setErrorCierre('')
    try {
      await api.descargarPaqueteDeCierre(clienteId!, anio, mes)
    } catch (e) {
      setErrorCierre(e instanceof Error ? e.message : 'No se pudo generar el paquete de cierre.')
    } finally {
      setGenerandoCierre(false)
    }
  }

  /** Exportar el reporte que está en pantalla (RF-705), con el comparativo elegido si lo hay. */
  async function exportar(formato: 'excel' | 'pdf') {
    setExportando(formato)
    setErrorExportar('')
    try {
      const id = clienteId!
      if (pestana === 'Balanza') await api.exportarBalanza(id, anio, mes, formato)
      else if (pestana === 'Mayor') await api.exportarLibroMayor(id, anio, mes, cuentaId, formato)
      else if (pestana === 'Diario') await api.exportarLibroDiario(id, anio, mes, formato)
      else if (pestana === 'Balance') {
        await (comparativo
          ? api.exportarBalanceGeneral(id, anio, mes, formato, comparativo.anio, comparativo.mes)
          : api.exportarBalanceGeneral(id, anio, mes, formato))
      } else {
        await (comparativo
          ? api.exportarEstadoDeResultados(id, anio, mes, formato, comparativo.anio, comparativo.mes)
          : api.exportarEstadoDeResultados(id, anio, mes, formato))
      }
    } catch (e) {
      setErrorExportar(e instanceof ErrorDeApi ? e.message : 'No se pudo exportar el reporte.')
    } finally {
      setExportando(undefined)
    }
  }

  if (error) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente) return <p>Cargando…</p>

  // En el mayor no hay nada que exportar mientras no se elija una cuenta.
  const sinNadaQueExportar = pestana === 'Mayor' && !cuentaId

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>

      <header className="encabezado linea-base">
        <h1>Reportes</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => {
            const estado = estadoPorPeriodo[`${p.anio}-${p.mes}`]
            const sufijo = estado === 'Cerrado' ? ' · cerrado' : estado === 'Abierto' ? ' · abierto (preliminar)' : ''
            return (
              <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>
                {nombreDePeriodo(p.anio, p.mes)}{sufijo}
              </option>
            )
          })}
        </select>
        <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>
        <span className="encabezado-acciones" data-recorrido="reportes-exportar">
          <button onClick={() => void exportar('excel')} disabled={exportando !== undefined || sinNadaQueExportar}>
            {exportando === 'excel' ? 'Exportando…' : 'Exportar .xlsx'}
          </button>
          <button onClick={() => void exportar('pdf')} disabled={exportando !== undefined || sinNadaQueExportar}>
            {exportando === 'pdf' ? 'Exportando…' : 'Exportar PDF'}
          </button>
          <button onClick={() => void descargarCierre()} disabled={generandoCierre}
            title="Balanza, balance general y estado de resultados en un ZIP (RF-903)">
            {generandoCierre ? 'Generando…' : 'Paquete de cierre'}
          </button>
        </span>
      </header>

      {errorExportar && <p className="error" role="alert">{errorExportar}</p>}
      {errorCierre && <p className="error" role="alert">{errorCierre}</p>}

      <div className="pestanas" role="tablist" aria-label="Reportes del periodo" data-recorrido="reportes-pestanas">
        {PESTANAS.map((p) => (
          <button
            key={p.clave}
            role="tab"
            id={`pestana-${p.clave}`}
            aria-selected={p.clave === pestana}
            aria-controls={`panel-${p.clave}`}
            className="pestana"
            onClick={() => setPestana(p.clave)}
          >
            {p.etiqueta}
          </button>
        ))}
        {/* El libro diario (RF-704) no compite con los cuatro reportes: es la bitácora de respaldo. */}
        <button
          className={pestana === 'Diario' ? 'enlace a-la-derecha acentuado' : 'enlace a-la-derecha'}
          onClick={() => setPestana('Diario')}
        >
          Libro diario{polizas ? ` · ${polizas.length} pólizas` : ''}
        </button>
      </div>

      {(pestana === 'Balance' || pestana === 'Resultados') && (
        <div className="encabezado" style={{ margin: '10px 0 0' }}>
          <SelectorDeComparativo anio={anio} mes={mes} comparativo={comparativo} onCambiar={setComparativo} />
        </div>
      )}

      <div
        id={`panel-${pestana}`}
        role={pestana === 'Diario' ? undefined : 'tabpanel'}
        aria-labelledby={pestana === 'Diario' ? undefined : `pestana-${pestana}`}
      >
        {pestana === 'Balanza' && <PestanaBalanza clienteId={clienteId} anio={anio} mes={mes} />}
        {pestana === 'Mayor' && (
          <PestanaMayor clienteId={clienteId} anio={anio} mes={mes} cuentaId={cuentaId} onCuenta={setCuentaId} />
        )}
        {pestana === 'Diario' && <PestanaDiario polizas={polizas} error={errorDiario} />}
        {pestana === 'Balance' && <PestanaBalance clienteId={clienteId} anio={anio} mes={mes} comparativo={comparativo} />}
        {pestana === 'Resultados' && <PestanaResultados clienteId={clienteId} anio={anio} mes={mes} comparativo={comparativo} />}
      </div>
    </>
  )
}

/**
 * Las cuentas visibles según qué cuentas acumulativas están plegadas: un renglón se ve si todos
 * sus ascendientes están desplegados. La balanza llega ordenada por número, así que el ascendiente
 * de un renglón es el anterior más cercano con un nivel menor; no hace falta el árbol completo.
 */
export function renglonesVisibles(renglones: RenglonDeBalanza[], plegadas: Set<string>): RenglonDeBalanza[] {
  const ascendientes: RenglonDeBalanza[] = []
  const visibles: RenglonDeBalanza[] = []

  for (const r of renglones) {
    while (ascendientes.length > 0 && ascendientes[ascendientes.length - 1].nivel >= r.nivel) ascendientes.pop()
    if (ascendientes.every((a) => !plegadas.has(a.cuentaId))) visibles.push(r)
    ascendientes.push(r)
  }
  return visibles
}

/** F-01: saldo inicial, cargos, abonos y saldo final por cuenta, por niveles y con su código agrupador. */
function PestanaBalanza({ clienteId, anio, mes }: { clienteId: string; anio: number; mes: number }) {
  const [balanza, setBalanza] = useState<Balanza>()
  const [error, setError] = useState('')
  // Se arranca con todo desplegado: la balanza se lee de corrido y plegar es una comodidad, no el estado normal.
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set())

  useEffect(() => {
    setPlegadas(new Set())
    api.balanza(clienteId, anio, mes)
      .then(setBalanza)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la balanza.'))
  }, [clienteId, anio, mes])

  if (error) return <p className="error" role="alert">{error}</p>
  if (!balanza) return <p className="vacio">Cargando…</p>

  const visibles = renglonesVisibles(balanza.renglones, plegadas)

  function alternar(cuentaId: string) {
    setPlegadas((previas) => {
      const siguientes = new Set(previas)
      if (!siguientes.delete(cuentaId)) siguientes.add(cuentaId)
      return siguientes
    })
  }

  return (
    <section className="reporte">
      <div className="reporte-cabecera">
        {balanza.cuadrada ? (
          <span className="insignia plazo suave">Cuadre verificado: sumas iguales ✓</span>
        ) : (
          <p className="error" style={{ margin: 0 }}>
            <strong>Balanza descuadrada (RN-01):</strong>{' '}
            cargos {dinero(balanza.totalCargos)} · abonos {dinero(balanza.totalAbonos)}
          </p>
        )}
        <span className="menuda">Por niveles: mayor → subcuenta → registro · clic en una cuenta acumulativa para plegarla</span>
      </div>

      <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Agrupador</th>
              <th className="num">Saldo inicial</th>
              <th className="num">Cargos</th>
              <th className="num">Abonos</th>
              <th className="num">Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((r) => (
              <tr
                key={r.cuentaId}
                className={r.esDeRegistro ? undefined : 'enlazable'}
                onClick={r.esDeRegistro ? undefined : () => alternar(r.cuentaId)}
              >
                <td data-nivel={r.nivel - 1} style={{ '--nivel': r.nivel - 1 } as CSSProperties}>
                  <span className="chevron" aria-hidden="true">
                    {r.esDeRegistro ? '' : plegadas.has(r.cuentaId) ? '▸' : '▾'}
                  </span>
                  <span className="mono secundaria">{r.numero}</span>{' '}
                  <span style={{ fontWeight: r.esDeRegistro ? 400 : 600 }}>{r.nombre}</span>
                </td>
                <td className="mono secundaria">{r.codigoAgrupadorSat ?? '—'}</td>
                <td className="num">{dinero(r.saldoInicial)}</td>
                <td className="num">{r.cargos ? dinero(r.cargos) : ''}</td>
                <td className="num">{r.abonos ? dinero(r.abonos) : ''}</td>
                <td className="num-fuerte">{dinero(r.saldoFinal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Solo cargos y abonos: son las dos sumas que RN-01 exige iguales. Los saldos de una
                cuenta deudora y una acreedora no se suman entre sí, así que esas celdas van vacías. */}
            <tr>
              <td colSpan={3}>Sumas iguales</td>
              <td className="num">{dinero(balanza.totalCargos)}</td>
              <td className="num">{dinero(balanza.totalAbonos)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="nota">
        El saldo inicial del mes coincide con el saldo final del mes anterior (arrastre automático); las
        cuentas acumulativas muestran la suma de sus subcuentas.
      </p>
    </section>
  )
}

/** F-02: detalle de movimientos de una cuenta con saldo acumulado, y rastreo a la póliza y su CFDI. */
function PestanaMayor({ clienteId, anio, mes, cuentaId, onCuenta }: {
  clienteId: string; anio: number; mes: number; cuentaId: string; onCuenta: (id: string) => void
}) {
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>([])
  const [mayor, setMayor] = useState<LibroMayor>()
  const [abierta, setAbierta] = useState<Poliza | null>(null)
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(abierta !== null, () => setAbierta(null))
  const [error, setError] = useState('')

  useEffect(() => {
    api.catalogoDeCuentas(clienteId)
      .then((todas) => setCuentas(todas.filter((c) => c.esDeRegistro)))
      .catch(() => setCuentas([]))
  }, [clienteId])

  useEffect(() => {
    if (!cuentaId) { setMayor(undefined); return }
    api.libroMayor(clienteId, anio, mes, cuentaId)
      .then(setMayor)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el libro mayor.'))
  }, [clienteId, anio, mes, cuentaId])

  async function verPoliza(polizaId: string) {
    try {
      setAbierta(await api.obtenerPoliza(clienteId, polizaId))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la póliza.')
    }
  }

  return (
    <section className="reporte">
      <div className="reporte-cabecera">
        <select className="compacta" aria-label="Cuenta" value={cuentaId}
          onChange={(e) => onCuenta(e.target.value)} style={{ width: 'auto', maxWidth: 420, margin: 0 }}>
          <option value="">— elige una cuenta de registro —</option>
          {cuentas.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>)}
        </select>
        <span className="menuda">Auxiliar de cuenta con detalle de movimientos y póliza de origen (RF-702)</span>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {mayor && (
        mayor.movimientos.length === 0 ? (
          <>
            <p className="sutil">Saldo inicial: <strong className="mono">{dinero(mayor.saldoInicial)}</strong></p>
            <p className="vacio">Sin movimientos en este periodo.</p>
          </>
        ) : (
          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Fecha</th><th>Póliza</th><th>Concepto</th>
                  <th className="num">Cargo</th><th className="num">Abono</th><th className="num">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {/* El saldo inicial es el primer renglón del auxiliar, no una nota aparte: así la
                    columna de saldo se lee de corrido desde el arrastre hasta el saldo final. */}
                <tr>
                  <td /><td />
                  <td className="secundaria">Saldo inicial</td>
                  <td /><td />
                  <td className="num-fuerte">{dinero(mayor.saldoInicial)}</td>
                </tr>
                {mayor.movimientos.map((m, i) => (
                  <tr key={i}>
                    <td>{m.fecha}</td>
                    <td><button className="enlace mono" onClick={() => void verPoliza(m.polizaId)}>{m.polizaFolioVisible}</button></td>
                    <td>{m.descripcion ?? ''}</td>
                    <td className="num">{m.cargo ? dinero(m.cargo) : ''}</td>
                    <td className="num">{m.abono ? dinero(m.abono) : ''}</td>
                    <td className="num">{dinero(m.saldoAcumulado)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>Saldo final</td>
                  <td className="num">{dinero(mayor.saldoFinal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {abierta && (
        <div className="modal" {...telon(() => setAbierta(null))}>
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTitulo}
            className="panel"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={idTitulo}>{abierta.folioVisible} · {abierta.concepto}</h2>
            <p className="sutil">
              {abierta.tipo} · {abierta.fecha}
              {abierta.cfdis.length > 0 && <> · CFDI: {abierta.cfdis.map((c) => c.uuid).join(', ')}</>}
            </p>
            <table className="tabla">
              <thead><tr><th>Cuenta</th><th className="num">Debe</th><th className="num">Haber</th></tr></thead>
              <tbody>
                {abierta.movimientos.map((m) => (
                  <tr key={m.id}>
                    <td><span className="mono">{m.cuentaNumero}</span> {m.cuentaNombre}</td>
                    <td className="num">{m.cargo ? dinero(m.cargo) : ''}</td>
                    <td className="num">{m.abono ? dinero(m.abono) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="acciones">
              <button onClick={() => setAbierta(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

/** F-03: las pólizas del periodo, en orden cronológico, con sus movimientos. */
function PestanaDiario({ polizas, error }: { polizas?: Poliza[]; error: string }) {
  if (error) return <p className="error" role="alert">{error}</p>
  if (!polizas) return <p className="vacio">Cargando…</p>

  return (
    <section className="reporte">
      {polizas.length === 0 && <p className="vacio">Sin pólizas en este periodo.</p>}
      {polizas.map((p) => (
        <table key={p.id} className="tabla" style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th colSpan={3}>
                {p.folioVisible} · {p.fecha} · {p.concepto}
                {p.estado === 'Cancelada' && ' · CANCELADA'}
              </th>
            </tr>
          </thead>
          <tbody>
            {p.movimientos.map((m) => (
              <tr key={m.id} className={p.estado === 'Cancelada' ? 'inactiva' : undefined}>
                <td><span className="mono">{m.cuentaNumero}</span> {m.cuentaNombre}{m.descripcion ? ` · ${m.descripcion}` : ''}</td>
                <td className="num" style={{ width: 120 }}>{m.cargo ? dinero(m.cargo) : ''}</td>
                <td className="num" style={{ width: 120 }}>{m.abono ? dinero(m.abono) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </section>
  )
}

/**
 * Variación entre el periodo y su comparativo (RF-703): el porcentaje, que es como se lee un
 * comparativo, y el monto cuando la base es cero y no hay porcentaje que calcular.
 */
function CeldaDeVariacion({ monto, montoComparativo, negativo }: {
  monto: number; montoComparativo: number; negativo?: boolean
}) {
  const variacion = monto - montoComparativo
  const porcentaje = montoComparativo !== 0 ? (variacion / Math.abs(montoComparativo)) * 100 : null
  // El acento marca lo que mejora. En costos y gastos, subir no mejora nada: van sin acento.
  const clase = negativo ? 'num' : variacion < 0 ? 'num atenuada' : 'num acentuada'
  return (
    <td className={clase}>
      {dinero(variacion)}
      {porcentaje !== null && <span className="sutil"> ({porcentaje > 0 ? '+' : ''}{porcentaje.toFixed(1)}%)</span>}
    </td>
  )
}

/** Renglón de cifras: periodo, comparativo y, si se pidió un comparativo explícito, la variación. */
function CeldasComparativas({ monto, montoComparativo, conVariacion, negativo }: {
  monto: number; montoComparativo: number; conVariacion: boolean; negativo?: boolean
}) {
  const cifra = (n: number) => (negativo ? enParentesis(n) : dinero(n))
  return (
    <>
      <td className="num">{cifra(monto)}</td>
      <td className="num">{cifra(montoComparativo)}</td>
      {conVariacion && <CeldaDeVariacion monto={monto} montoComparativo={montoComparativo} negativo={negativo} />}
    </>
  )
}

/** Una sección con renglones y su comparativo (usada tanto por Balance como por Resultados). */
function TablaDeSeccion({ seccion, negrita, conVariacion, negativo }: {
  seccion: SeccionComparativa; negrita?: boolean; conVariacion: boolean; negativo?: boolean
}) {
  if (seccion.renglones.length === 0 && seccion.total === 0 && seccion.totalComparativo === 0) return null
  return (
    <>
      <tr><td colSpan={conVariacion ? 4 : 3} style={{ fontWeight: 700, paddingTop: 10 }}>{seccion.nombre}</td></tr>
      {seccion.renglones.map((r) => (
        <tr key={r.numero}>
          <td style={{ paddingLeft: 20 }}>{r.numero} · {r.nombre}</td>
          <CeldasComparativas monto={r.monto} montoComparativo={r.montoComparativo} conVariacion={conVariacion} negativo={negativo} />
        </tr>
      ))}
      <tr style={{ fontWeight: negrita ? 700 : 600 }}>
        <td style={{ paddingLeft: 20 }}>Total {seccion.nombre.toLowerCase()}</td>
        <CeldasComparativas monto={seccion.total} montoComparativo={seccion.totalComparativo} conVariacion={conVariacion} negativo={negativo} />
      </tr>
    </>
  )
}

/** Renglón de total (activo, pasivo, utilidades…) con las mismas columnas que las secciones. */
function RenglonDeTotal({ etiqueta, monto, montoComparativo, conVariacion }: {
  etiqueta: string; monto: number; montoComparativo: number; conVariacion: boolean
}) {
  return (
    <tr style={{ fontWeight: 700 }}>
      <td>{etiqueta}</td>
      <CeldasComparativas monto={monto} montoComparativo={montoComparativo} conVariacion={conVariacion} />
    </tr>
  )
}

function EncabezadoComparativo({ anio, mes, anioComparativo, mesComparativo, conVariacion, concepto }: {
  anio: number; mes: number; anioComparativo: number; mesComparativo: number; conVariacion: boolean; concepto?: string
}) {
  return (
    <thead>
      <tr>
        <th>{concepto ?? 'Concepto'}</th>
        <th className="num">{nombreDePeriodo(anio, mes)}</th>
        <th className="num">{nombreDePeriodo(anioComparativo, mesComparativo)}</th>
        {conVariacion && <th className="num">Variación</th>}
      </tr>
    </thead>
  )
}

type PropsDeEstadoFinanciero = { clienteId: string; anio: number; mes: number; comparativo?: PeriodoComparativo }

/**
 * Nota de congruencia global (RF-706), en las dos pantallas que dependen de la utilidad. La cifra del capital viaja
 * con las cuentas que la forman: en un catálogo de la plantilla anterior al 20/10/2026, «Utilidad de ejercicios
 * anteriores» tenía el agrupador 305.01 y se sumaría como si fuera del año. Sin cuenta con agrupador 305 no hay
 * contra qué comparar, y la nota lo dice en vez de mostrar un cero.
 */
function NotaDeCongruencia({ congruencia }: { congruencia: CongruenciaGlobal }) {
  const cuentas = congruencia.cuentasDelResultadoDelEjercicio
  // Primero la falta de cuenta 305: sin ella, 0 contra 0 no es «coincide», es «no hay contra qué comparar».
  if (cuentas.length === 0) {
    return (
      <p className="error">
        <strong>Congruencia global (RF-706):</strong> la utilidad del estado de resultados
        ({dinero(congruencia.utilidadSegunEstadoDeResultados)}) no se puede comparar con el capital: ninguna cuenta del
        catálogo tiene el código agrupador 305 «Resultado del ejercicio». Asígnalo a la cuenta donde registras el resultado
        del año.
      </p>
    )
  }
  if (congruencia.utilidadCongruente) {
    return (
      <p className="sutil">
        <strong>Congruencia global (RF-706):</strong> la utilidad del estado de resultados coincide con la del capital del balance.
      </p>
    )
  }
  return (
    <p className="error">
      <strong>Congruencia global (RF-706):</strong>{' '}
      {`la utilidad del estado de resultados (${dinero(congruencia.utilidadSegunEstadoDeResultados)}) no coincide con ` +
        `la registrada en el capital del balance (${dinero(congruencia.utilidadSegunBalance)}); revisa si falta la póliza de cierre. ` +
        `La del capital suma ${cuentas.map((c) => `${c.numero} · ${c.nombre} (agrupador ${c.codigoAgrupadorSat}, ${dinero(c.saldo)})`).join(', ')}: ` +
        'si alguna no es del resultado del año, corrige su agrupador en el catálogo.'}
    </p>
  )
}

/** F-04, RN-13: activo y pasivo (circulante/no circulante) y capital, con comparativo. */
function PestanaBalance({ clienteId, anio, mes, comparativo }: PropsDeEstadoFinanciero) {
  const [balance, setBalance] = useState<BalanceGeneral>()
  const [error, setError] = useState('')
  const { congruencia, errorCongruencia } = useCongruencia(clienteId, anio, mes)
  const conVariacion = comparativo !== undefined

  useEffect(() => {
    api.balanceGeneral(clienteId, anio, mes, comparativo?.anio, comparativo?.mes)
      .then(setBalance)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el balance general.'))
  }, [clienteId, anio, mes, comparativo])

  if (error) return <p className="error" role="alert">{error}</p>
  if (!balance) return <p className="vacio">Cargando…</p>

  const cabecera = (concepto: string) => (
    <EncabezadoComparativo
      anio={anio} mes={mes} concepto={concepto}
      anioComparativo={balance.anioComparativo} mesComparativo={balance.mesComparativo} conVariacion={conVariacion}
    />
  )

  return (
    <section className="reporte reporte-bg">
      <div className="reporte-cabecera">
        {balance.cuadrado ? (
          <span className="insignia plazo suave">Activo = Pasivo + Capital ✓</span>
        ) : (
          <p className="error" style={{ margin: 0 }}>
            <strong>Balance descuadrado:</strong>{' '}
            activo {dinero(balance.totalActivo)} · pasivo + capital {dinero(balance.totalPasivoMasCapital)}
          </p>
        )}
        <span className="menuda">Estructura NIF por clasificación del código agrupador (RN-13)</span>
      </div>

      {errorCongruencia && <p className="error" role="alert">{errorCongruencia}</p>}
      {congruencia && <NotaDeCongruencia congruencia={congruencia} />}

      {/* Dos tablas lado a lado, como se lee un balance. Con comparativo explícito cada tabla
          crece a cuatro columnas de cifras, así que se apilan para que quepan. */}
      <div className={conVariacion ? 'balance-nif apilado' : 'balance-nif'}>
        <table className="tabla">
          {cabecera('Activo')}
          <tbody>
            <TablaDeSeccion seccion={balance.activoCirculante} conVariacion={conVariacion} />
            <TablaDeSeccion seccion={balance.activoNoCirculante} conVariacion={conVariacion} />
          </tbody>
          <tfoot>
            <RenglonDeTotal etiqueta="Total activo" monto={balance.totalActivo} montoComparativo={balance.totalActivoComparativo} conVariacion={conVariacion} />
          </tfoot>
        </table>

        <table className="tabla">
          {cabecera('Pasivo y capital')}
          <tbody>
            <TablaDeSeccion seccion={balance.pasivoCirculante} conVariacion={conVariacion} />
            <TablaDeSeccion seccion={balance.pasivoNoCirculante} conVariacion={conVariacion} />
            <RenglonDeTotal etiqueta="Total pasivo" monto={balance.totalPasivo} montoComparativo={balance.totalPasivoComparativo} conVariacion={conVariacion} />
            <TablaDeSeccion seccion={balance.capital} conVariacion={conVariacion} />
          </tbody>
          <tfoot>
            <RenglonDeTotal etiqueta="Total pasivo + capital" monto={balance.totalPasivoMasCapital} montoComparativo={balance.totalPasivoMasCapitalComparativo} conVariacion={conVariacion} />
          </tfoot>
        </table>
      </div>
    </section>
  )
}

/** F-04: ingresos, costos y gastos del mes, con comparativo. */
function PestanaResultados({ clienteId, anio, mes, comparativo }: PropsDeEstadoFinanciero) {
  const [resultados, setResultados] = useState<EstadoDeResultados>()
  const [error, setError] = useState('')
  const { congruencia } = useCongruencia(clienteId, anio, mes)

  useEffect(() => {
    api.estadoDeResultados(clienteId, anio, mes, comparativo?.anio, comparativo?.mes)
      .then(setResultados)
      .catch((e) => setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el estado de resultados.'))
  }, [clienteId, anio, mes, comparativo])

  if (error) return <p className="error" role="alert">{error}</p>
  if (!resultados) return <p className="vacio">Cargando…</p>

  return (
    <section className="reporte reporte-er">
      <div className="reporte-cabecera">
        <span className="menuda">Comparativo entre periodos (RF-703)</span>
      </div>
      <div className="tabla-envoltura">
        <table className="tabla">
          {/* El estado de resultados siempre compara: sin variación no se sabe si el mes fue bueno. */}
          <EncabezadoComparativo anio={anio} mes={mes} anioComparativo={resultados.anioComparativo} mesComparativo={resultados.mesComparativo} conVariacion />
          <tbody>
            <TablaDeSeccion seccion={resultados.ingresos} conVariacion />
            <TablaDeSeccion seccion={resultados.costos} conVariacion negativo />
            <RenglonDeTotal etiqueta="Utilidad bruta" monto={resultados.utilidadBruta} montoComparativo={resultados.utilidadBrutaComparativo} conVariacion />
            <TablaDeSeccion seccion={resultados.gastos} conVariacion negativo />
            <RenglonDeTotal etiqueta="Utilidad de operación" monto={resultados.utilidadDeOperacion} montoComparativo={resultados.utilidadDeOperacionComparativo} conVariacion />
            <TablaDeSeccion seccion={resultados.resultadoIntegralDeFinanciamiento} conVariacion />
          </tbody>
          <tfoot>
            <RenglonDeTotal etiqueta="Utilidad del periodo" monto={resultados.utilidadNeta} montoComparativo={resultados.utilidadNetaComparativo} conVariacion />
          </tfoot>
        </table>
      </div>
      {congruencia?.utilidadCongruente
        ? <p className="nota">La utilidad es congruente con la balanza del periodo (RF-706).</p>
        : congruencia && <NotaDeCongruencia congruencia={congruencia} />}
    </section>
  )
}
